<template>
  <page-template :is-loading="isLoading" :data-exists="selectedMobileApplicationGroupData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('mobileApplicationGroup.configPage.newPageHeadline')
          : $t('mobileApplicationGroup.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft>
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Mobile Application Groups List"
        class="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedMobileApplicationGroup && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name])"
          :disabled="isLoading"
          v-model:value="selectedMobileApplicationGroup"
          @update:value="switchMobileApplicationGroup($event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('mobileApplicationGroup.configPage.switchTooltip')"
          classes="switch-mobile-application-group switch-page-entry"
          data-qa="switch-mobile-application-group"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight>
      <template v-if="selectedMobileApplicationGroup && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateMobileApplicationGroup()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadMobileApplicationGroup()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteMobileApplicationGroupModal()"
          class="delete-button"
          :disabled="isLoading || selectedMobileApplicationGroupReferenced"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isMobileApplicationGroupValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('mobileApplicationGroup.configPage.name')"
              :placeholder="$t('mobileApplicationGroup.configPage.name')"
              v-model:inputValue="selectedMobileApplicationGroupData.name"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-input"
              :label="$t('mobileApplicationGroup.configPage.description')"
              v-model:inputValue="selectedMobileApplicationGroupData.description"
              rows="2"
            />
          </div>
        </div>
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="token-header-name"
              classes="token-header-name"
              data-qa="token-header-name-input"
              :label="$t('mobileApplicationGroup.configPage.tokenHeaderName')"
              :placeholder="$t('mobileApplicationGroup.configPage.tokenHeaderName')"
              :is-invalid="tokenHeaderNameErrors !== ''"
              :error-message-text="tokenHeaderNameErrors"
              v-model:inputValue="selectedMobileApplicationGroupData['uid_header']"
            >
              <template #postLabel>
                <span
                  v-b-tooltip="
                    $t('mobileApplicationGroup.configPage.tokenHeaderNameAdditionalInfoTooltip')
                  "
                  class="info-icon-wrapper"
                >
                  <icon-info-small color="dusty-gray" class="info-icon" />
                </span>
              </template>
            </rbz-form-input>
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="grace-period"
              classes="grace-period"
              data-qa="grace-period-input"
              suffix="seconds"
              :label="$t('mobileApplicationGroup.configPage.gracePeriod')"
              :placeholder="$t('mobileApplicationGroup.configPage.gracePeriod')"
              :is-invalid="gracePeriodErrors !== ''"
              :error-message-text="gracePeriodErrors"
              v-model:inputValue="selectedMobileApplicationGroupData.grace"
            >
              <template #postLabel>
                <span
                  v-b-tooltip="
                    $t('mobileApplicationGroup.configPage.gracePeriodAdditionalInfoTooltip')
                  "
                  class="info-icon-wrapper"
                >
                  <icon-info-small color="dusty-gray" class="info-icon" />
                </span>
              </template>
            </rbz-form-input>
          </div>
        </div>
      </div>
      <div class="rbz-field rbz-table-field">
        <rbz-table
          :columns="appSignaturesColumns"
          :table-data="selectedMobileApplicationGroupData.signatures"
          :row-buttons="appSignaturesRowButtons"
          :show-new-as-row="true"
          :show-new-default-data="defaultNewAppSignature"
          :show-filter-button="false"
          :show-download-button="false"
          rows-per-page="20"
          ref="appSignaturesTable"
          class="app-signatures-table"
          data-qa="app-signatures-table"
          @add-inline-data="addAppSignature"
          @edit-inline-data="editAppSignature"
          @delete-row-button-clicked="deleteAppSignature"
        >
          <template #tableResultsOverride>
            <span class="app-signatures-table-title rbz-text-regular">
              {{ $t('mobileApplicationGroup.configPage.appSignaturesTable.title') }}
            </span>
          </template>
          <template #activeOverrideNewInput="rowProps">
            <rbz-form-toggle
              v-model:buttonValue="rowProps.data.active"
              :label="
                rowProps.data.active
                  ? $t('mobileApplicationGroup.configPage.appSignaturesTable.activeMode')
                  : ''
              "
            />
          </template>
          <template #activeCell="cellProps">
            <rbz-form-toggle
              v-model:buttonValue="cellProps.row.active"
              :label="
                cellProps.row.active
                  ? $t('mobileApplicationGroup.configPage.appSignaturesTable.activeMode')
                  : ''
              "
            />
          </template>
        </rbz-table>
      </div>
      <div class="rbz-field rbz-table-field">
        <rbz-table
          :columns="profilesColumns"
          :table-data="selectedMobileApplicationGroupData['active_config']"
          :row-buttons="profilesRowButtons"
          :show-new-default-data="defaultNewProfile"
          :show-new-as-row="true"
          :show-filter-button="false"
          :show-download-button="false"
          :show-expander-column="true"
          rows-per-page="20"
          ref="profilesTable"
          class="profiles-table"
          data-qa="profiles-table"
          @add-inline-data="addProfile"
          @edit-inline-data="editProfile"
          @delete-row-button-clicked="deleteProfile"
        >
          <template #tableResultsOverride>
            <span class="profiles-table-title rbz-text-regular">
              {{ $t('mobileApplicationGroup.configPage.profilesTable.title') }}
            </span>
          </template>
          <template #rowExpander="rowProps">
            <div class="rbz-columns profiles-table-expanded-row">
              <div class="rbz-column is-5">
                <div class="rbz-field">
                  <rbz-form-input
                    name="map-entry-name-input"
                    data-qa="map-entry-name-input"
                    classes="map-entry-name-input"
                    :placeholder="$t('mobileApplicationGroup.configPage.profilesTable.name')"
                    :disabled="isProfileMapEntryProtected(rowProps.row)"
                    v-model:inputValue="rowProps.row.name"
                  />
                </div>
              </div>
              <div class="rbz-column is-4">
                <div class="rbz-field">
                  <rbz-form-textarea
                    name="new-row-json-input"
                    classes="new-row-json-input"
                    data-qa="json-input"
                    :is-invalid="!validateProfileJson(rowProps.row)"
                    :error-message-text="getErrorsProfileJson(rowProps.row)"
                    :disabled="isProfileMapEntryProtected(rowProps.row)"
                    v-model:inputValue="rowProps.row.json"
                    rows="6"
                  />
                </div>
              </div>
            </div>
          </template>
          <template #jsonOverrideNewInput="rowProps">
            <rbz-form-textarea
              name="new-row-json-input"
              classes="new-row-json-input"
              data-qa="json-input"
              :is-invalid="!validateProfileJson(rowProps.data)"
              :error-message-text="getErrorsProfileJson(rowProps.data)"
              v-model:inputValue="rowProps.data.json"
              rows="6"
            />
          </template>
          <template #activeOverrideNewInput="rowProps">
            <rbz-form-toggle
              v-if="rowProps.mode === 'new'"
              :id="`profile-${rowProps.data.id}`"
              :buttonValue="true"
              :label="
                rowProps.data.active
                  ? $t('mobileApplicationGroup.configPage.appSignaturesTable.activeMode')
                  : ''
              "
              :disabled="true"
            />
            <rbz-form-toggle
              v-if="rowProps.mode === 'edit'"
              :id="`profile-${rowProps.data.id}`"
              v-model:buttonValue="rowProps.data.active"
              :label="
                rowProps.data.active
                  ? $t('mobileApplicationGroup.configPage.appSignaturesTable.activeMode')
                  : ''
              "
              :disabled="rowProps.data.active"
              @update:buttonValue="activateProfile(rowProps.data)"
            />
          </template>
          <template #activeCell="cellProps">
            <rbz-form-toggle
              name="active-profile"
              classes="disabled-hidden"
              :id="`profile-${cellProps.row.id}`"
              v-model:buttonValue="cellProps.row.active"
              :label="
                cellProps.row.active
                  ? $t('mobileApplicationGroup.configPage.appSignaturesTable.activeMode')
                  : ''
              "
              :disabled="cellProps.row.active"
              @update:buttonValue="activateProfile(cellProps.row)"
            />
          </template>
        </rbz-table>
      </div>
      <rbz-modal-dialog
        :show="showDeleteMobileApplicationGroupModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteMobileApplicationGroup"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteMobileApplicationGroupModal = false"
        @closeModal="showDeleteMobileApplicationGroupModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', {
              name: selectedMobileApplicationGroupData.name,
            })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import {
  BasicDocument,
  DocumentName,
  MobileApplicationGroup,
  MobileApplicationGroupProfile,
  MobileApplicationGroupSignature,
  ServerGroup,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'MobileApplicationGroupsEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    RbzFormInput,
    RbzFormTextarea,
    RbzFormToggle,
    IconInfoSmall,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzFormButton,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedMobileApplicationGroup: null as string,
      selectedMobileApplicationGroupData: null as MobileApplicationGroup,
      originalMobileApplicationGroupData: null as MobileApplicationGroup,
      pageLoadingCounter: null,
      isDeleteLoading: false,
      showDeleteMobileApplicationGroupModal: false,

      referencedIds: [],

      appSignaturesColumns: [
        {
          id: 'name',
          title: this.$t('mobileApplicationGroup.configPage.appSignaturesTable.signatureName'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-260px',
          cellContentClasses: 'ellipsis',
          validate: this.validateAppSignatureName,
          errorMessage: this.getErrorsAppSignatureName,
        },
        {
          id: 'hash',
          title: this.$t('mobileApplicationGroup.configPage.appSignaturesTable.hash'),
          fieldNames: ['hash'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          validate: this.validateAppSignatureHash,
          errorMessage: this.getErrorsAppSignatureHash,
        },
        {
          id: 'active',
          title: this.$t('mobileApplicationGroup.configPage.appSignaturesTable.status'),
          fieldNames: ['active'],
          isSortable: true,
          isSearchable: true,
          isSortByOriginalValue: true,
          displaySlotName: 'activeCell',
          overrideNewInput: 'activeOverrideNewInput',
          tooltipFunction: (item: MobileApplicationGroupSignature) => {
            return item.active ? this.$t('common.yes') : this.$t('common.no');
          },
          tooltipFunctionAlwaysDisplayed: true,
          classes: 'width-200px',
        },
      ] as RbzTableColumnOptions[],
      appSignaturesRowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          editInline: true,
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      defaultNewAppSignature: {
        name: '',
        hash: '',
        active: true,
      },

      profilesColumns: [
        {
          id: 'name',
          title: this.$t('mobileApplicationGroup.configPage.profilesTable.name'),
          fieldNames: ['name'],
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          validate: this.validateProfileName,
          errorMessage: this.getErrorsProfileName,
        },
        {
          id: 'json',
          title: this.$t('mobileApplicationGroup.configPage.profilesTable.json'),
          hideTitle: true,
          fieldNames: ['json'],
          overrideNewInput: 'jsonOverrideNewInput',
          displayFunction: () => '',
          classes: 'width-500px',
          validate: this.validateProfileJson,
          errorMessage: this.getErrorsProfileJson,
        },
        {
          id: 'active',
          title: this.$t('mobileApplicationGroup.configPage.profilesTable.active'),
          fieldNames: ['active'],
          isSearchable: true,
          isSortByOriginalValue: true,
          displaySlotName: 'activeCell',
          overrideNewInput: 'activeOverrideNewInput',
          tooltipFunction: (item: MobileApplicationGroupProfile) => {
            return item.active ? this.$t('common.yes') : this.$t('common.no');
          },
          tooltipFunctionAlwaysDisplayed: true,
          classes: 'width-200px',
        },
      ] as RbzTableColumnOptions[],
      profilesRowButtons: [
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: (profile: MobileApplicationGroupProfile) => {
            return this.isProfileMapEntryProtected(profile);
          },
          tooltipCallback: (profile: MobileApplicationGroupProfile) => {
            return this.isProfileMapEntryProtected(profile)
              ? this.$t('mobileApplicationGroup.configPage.profilesTable.deleteDisabledTooltip')
              : this.$t('common.operations.delete.tableTooltip');
          },
        },
      ] as RbzTableRowButton[],
      defaultNewProfile: {
        name: '',
        json: '',
        active: true,
      },
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('mobileApplicationGroup.plural'),
        singular: this.$t('mobileApplicationGroup.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    entityNotSaved(): boolean {
      return !_.isEqual(
        this.selectedMobileApplicationGroupData,
        this.originalMobileApplicationGroupData
      );
    },

    selectedMobileApplicationGroupReferenced(): boolean {
      return this.referencedIds.includes(this.selectedMobileApplicationGroup);
    },

    deleteTooltip(): string {
      if (this.selectedMobileApplicationGroupReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    tokenHeaderNameErrors(): string {
      const tokenHeaderName = this.selectedMobileApplicationGroupData['uid_header'].trim();
      if (!new RegExp(/^[0-9a-zA-Z-_]*$/).test(tokenHeaderName)) {
        return this.$t('mobileApplicationGroup.configPage.errors.tokenHeaderNameInvalid');
      }
      return '';
    },

    gracePeriodErrors(): string {
      const gracePeriod = this.selectedMobileApplicationGroupData.grace.trim();
      if (gracePeriod === '') {
        return this.$t('mobileApplicationGroup.configPage.errors.gracePeriodEmpty');
      }
      if (!new RegExp(/^\d+$/).test(gracePeriod)) {
        return this.$t('mobileApplicationGroup.configPage.errors.gracePeriodNotInteger');
      }
      return '';
    },

    isMobileApplicationGroupValid(): boolean {
      const trimmedName = this.selectedMobileApplicationGroupData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedMobileApplicationGroupData.name &&
        (trimmedName === this.originalMobileApplicationGroupData.name.toLowerCase().trim() ||
          !nameExists);

      const tokenHeaderNameValid = this.tokenHeaderNameErrors === '';
      const gracePeriodValid = this.gracePeriodErrors === '';

      return nameValid && tokenHeaderNameValid && gracePeriodValid;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('mobile-application-groups'));
    },

    switchMobileApplicationGroup(mobileApplicationGroupId: string) {
      this.navigateTo(this.getEditPath('mobile-application-groups', mobileApplicationGroupId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/mobile-application-groups`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newMobileApplicationGroup(): MobileApplicationGroup {
      const factory = DatasetsUtils.newEntityFactory['mobile-application-groups'];
      return factory && factory();
    },

    async loadMobileApplicationGroup(mobileApplicationGroupId: string) {
      this.setPageLoadingStatus(true);
      this.selectedMobileApplicationGroup = mobileApplicationGroupId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/mobile-application-groups/${mobileApplicationGroupId}`,
      });
      this.selectedMobileApplicationGroupData = response?.data || {};
      _.forEach(this.selectedMobileApplicationGroupData.signatures, (appSignature) => {
        appSignature.id = DatasetsUtils.generateUUID2();
      });
      _.forEach(this.selectedMobileApplicationGroupData['active_config'], (profile) => {
        profile.id = DatasetsUtils.generateUUID2();
      });
      this.originalMobileApplicationGroupData = _.cloneDeep(
        this.selectedMobileApplicationGroupData
      );
      this.setPageLoadingStatus(false);
    },

    async duplicateMobileApplicationGroup() {
      this.setPageLoadingStatus(true);
      const mobileApplicationGroupToAdd = _.cloneDeep(this.selectedMobileApplicationGroupData);
      mobileApplicationGroupToAdd['signatures'].forEach((signature) => {
        delete signature.id;
      });
      mobileApplicationGroupToAdd['active_config'].forEach((profile) => {
        delete profile.id;
      });
      mobileApplicationGroupToAdd.id = DatasetsUtils.generateUUID2();
      mobileApplicationGroupToAdd.name = Utils.generateUniqueEntityName(
        mobileApplicationGroupToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/mobile-application-groups/${mobileApplicationGroupToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: mobileApplicationGroupToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchMobileApplicationGroup(mobileApplicationGroupToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadMobileApplicationGroup() {
      const mobileApplicationGroupToDownload = _.cloneDeep(this.selectedMobileApplicationGroupData);
      mobileApplicationGroupToDownload['signatures'].forEach((signature) => {
        delete signature.id;
      });
      mobileApplicationGroupToDownload['active_config'].forEach((profile) => {
        delete profile.id;
      });
      Utils.downloadFile('mobile-application-group', 'json', mobileApplicationGroupToDownload);
    },

    openDeleteMobileApplicationGroupModal() {
      this.showDeleteMobileApplicationGroupModal = true;
    },

    async deleteMobileApplicationGroup() {
      this.isDeleteLoading = true;
      const mobileApplicationGroupId = this.selectedMobileApplicationGroup;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === mobileApplicationGroupId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedMobileApplicationGroup = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/mobile-application-groups/${mobileApplicationGroupId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedMobileApplicationGroupData.name =
        this.selectedMobileApplicationGroupData.name.trim();
      this.selectedMobileApplicationGroupData['uid_header'] =
        this.selectedMobileApplicationGroupData['uid_header'].trim();
      this.selectedMobileApplicationGroupData.grace =
        this.selectedMobileApplicationGroupData.grace.trim();
      const mobileApplicationGroupToSave = _.cloneDeep(this.selectedMobileApplicationGroupData);
      mobileApplicationGroupToSave['signatures'].forEach((signature) => {
        delete signature.id;
      });
      mobileApplicationGroupToSave['active_config'].forEach((profile) => {
        delete profile.id;
      });
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/mobile-application-groups/${this.selectedMobileApplicationGroupData.id}`,
        data: mobileApplicationGroupToSave,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedMobileApplicationGroupData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalMobileApplicationGroupData = _.cloneDeep(
            this.selectedMobileApplicationGroupData
          );
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
      });
      const serverGroups = response?.data?.items || [];
      const referencedIds: string[] = [];
      _.forEach(serverGroups, (serverGroup: ServerGroup) => {
        referencedIds.push(serverGroup['mobile_application_group']);
      });
      this.referencedIds = _.uniq(referencedIds);
      this.setPageLoadingStatus(false);
    },

    addAppSignature(newAppSignature: MobileApplicationGroupSignature) {
      newAppSignature.id = DatasetsUtils.generateUUID2();
      newAppSignature.hash = newAppSignature.hash.replace(/[\s:]+/g, '').trim();
      this.selectedMobileApplicationGroupData.signatures.push(newAppSignature);
    },

    editAppSignature(newAppSignature: MobileApplicationGroupSignature) {
      const oldAppSignatureIndex = _.findIndex(
        this.selectedMobileApplicationGroupData.signatures,
        (appSignature) => {
          return appSignature.id === newAppSignature.id;
        }
      );
      newAppSignature.hash = newAppSignature.hash.replace(/[\s:]+/g, '').trim();
      this.selectedMobileApplicationGroupData.signatures[oldAppSignatureIndex] = newAppSignature;
    },

    deleteAppSignature(appSignature: MobileApplicationGroupSignature) {
      const id = appSignature.id;
      const index = _.findIndex(this.selectedMobileApplicationGroupData.signatures, (item) => {
        return item.id === id;
      });
      this.selectedMobileApplicationGroupData.signatures.splice(index, 1);
    },

    validateAppSignatureName(appSignature: MobileApplicationGroupSignature) {
      return this.getErrorsAppSignatureName(appSignature) === '';
    },

    getErrorsAppSignatureName(appSignature: MobileApplicationGroupSignature) {
      const appSignatures = this.selectedMobileApplicationGroupData.signatures;
      const name = appSignature.name.toLowerCase().trim();
      if (name === '') {
        return this.$t('mobileApplicationGroup.configPage.appSignaturesTable.errors.nameEmpty');
      }
      const isDuplicate = appSignatures.some((signature) => {
        return signature.name.toLowerCase() === name && signature.id !== appSignature.id;
      });
      if (isDuplicate) {
        return this.$t('mobileApplicationGroup.configPage.appSignaturesTable.errors.nameDuplicate');
      }
      return '';
    },

    validateAppSignatureHash(appSignature: MobileApplicationGroupSignature) {
      return this.getErrorsAppSignatureHash(appSignature) === '';
    },

    getErrorsAppSignatureHash(appSignature: MobileApplicationGroupSignature) {
      const appSignatures = this.selectedMobileApplicationGroupData.signatures;
      const hash = appSignature.hash
        .toLowerCase()
        .replace(/[\s:]+/g, '')
        .trim();
      const isHex = hash.match(new RegExp(/^[0-9a-f]+$/));
      if (!isHex) {
        return this.$t('mobileApplicationGroup.configPage.appSignaturesTable.errors.hashHex');
      }
      const isRepeat = appSignatures.some((signature) => {
        return signature.hash.slice(0, 7) === hash.slice(0, 7) && signature.id !== appSignature.id;
      });
      if (isRepeat) {
        return this.$t('mobileApplicationGroup.configPage.appSignaturesTable.errors.hashRepeat');
      }
      return '';
    },

    addProfile(newProfile: MobileApplicationGroupProfile) {
      newProfile.id = DatasetsUtils.generateUUID2();
      newProfile.json = JSON.stringify(JSON.parse(newProfile.json));
      this.selectedMobileApplicationGroupData['active_config'].push(newProfile);
      this.activateProfile(newProfile);
    },

    editProfile(newProfile: MobileApplicationGroupProfile) {
      const oldProfileIndex = _.findIndex(
        this.selectedMobileApplicationGroupData['active_config'],
        (profile) => {
          return profile.id === newProfile.id;
        }
      );
      newProfile.json = JSON.stringify(JSON.parse(newProfile.json));
      this.selectedMobileApplicationGroupData['active_config'][oldProfileIndex] = newProfile;
    },

    deleteProfile(profile: MobileApplicationGroupProfile) {
      const id = profile.id;
      const index = _.findIndex(
        this.selectedMobileApplicationGroupData['active_config'],
        (item) => {
          return item.id === id;
        }
      );
      this.selectedMobileApplicationGroupData['active_config'].splice(index, 1);
    },

    activateProfile(profile: MobileApplicationGroupProfile) {
      if (profile) {
        this.selectedMobileApplicationGroupData['active_config'].forEach((item) => {
          item.active = item.id === profile.id;
          if (Object.keys(this.$refs.profilesTable.rowsInEditMode).includes(item.id)) {
            this.$refs.profilesTable.rowsInEditMode[item.id].active = item.active;
          }
        });
      }
    },

    validateProfileName(profile: MobileApplicationGroupProfile) {
      return this.getErrorsProfileName(profile) === '';
    },

    getErrorsProfileName(profile: MobileApplicationGroupProfile) {
      const profiles = this.selectedMobileApplicationGroupData['active_config'];
      const name = profile.name.toLowerCase();
      if (name === '') {
        return this.$t('mobileApplicationGroup.configPage.profilesTable.errors.nameEmpty');
      }
      if (
        _.some(profiles, (prof) => {
          return prof.name.toLowerCase().trim() === name.trim() && prof.id !== profile.id;
        })
      ) {
        return this.$t('mobileApplicationGroup.configPage.profilesTable.errors.nameDuplicate');
      }
      return '';
    },

    validateProfileJson(profile: MobileApplicationGroupProfile) {
      return this.getErrorsProfileJson(profile) === '';
    },

    getErrorsProfileJson(profile: MobileApplicationGroupProfile) {
      const json = profile.json;
      let valid = false;
      if (json[0] === '{' && json[json.length - 1] === '}') {
        try {
          JSON.parse(`${json}`);
          valid = true;
        } catch (e) {
          return this.$t('mobileApplicationGroup.configPage.profilesTable.errors.jsonInvalid');
        }
      }
      if (!valid) {
        return this.$t('mobileApplicationGroup.configPage.profilesTable.errors.jsonInvalid');
      }
      return '';
    },

    isProfileMapEntryProtected(profile: MobileApplicationGroupProfile): boolean {
      return profile.name === 'Default' || profile.active;
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedMobileApplicationGroupData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (
          ['New Mobile Application Group', 'Mobile Application Groups Editor'].includes(routeName)
        ) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedMobileApplicationGroupData = this.newMobileApplicationGroup();
            this.selectedMobileApplicationGroupData.name = Utils.generateUniqueEntityName(
              this.selectedMobileApplicationGroupData.name,
              _.map(this.dropdownData, 'name')
            );
            _.forEach(this.selectedMobileApplicationGroupData['active_config'], (profile) => {
              profile.id = DatasetsUtils.generateUUID2();
            });
            this.originalMobileApplicationGroupData = _.cloneDeep(
              this.selectedMobileApplicationGroupData
            );
          } else {
            const mobileApplicationGroupId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === mobileApplicationGroupId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadMobileApplicationGroup(mobileApplicationGroupId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedMobileApplicationGroupData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

:deep(.profiles-table) {
  .addition-row td {
    vertical-align: baseline;
  }

  .switch-button-holder.disabled.disabled-hidden {
    .toggle-input + .slider {
      opacity: 1;
    }

    .label {
      color: map.get(colors.$colors, 'cod-gray');
    }
  }

  .profiles-table-expanded-row {
    gap: 24px;
  }
}
</style>
